import { statusColors } from 'utils/variables/statusColors';
import styled, { css } from 'styled-components';
import { Input } from 'reactstrap';

export const MainContainer = styled.main`
  display: ${({ isQueueLayout }) => isQueueLayout && 'grid'};
  grid-template-columns: max-content 1fr;
  column-gap: 1rem;
  padding: 0.5rem;
  background-color: #eee;
  min-height: 100dvh;
`;

export const StatsCardsContainer = styled.div`
  --row-gap: 0.5rem;

  grid-column: 1;
  display: grid;
  row-gap: var(--row-gap);
  grid-auto-rows: min-content;
  align-content: start;
  height: calc(100vh - 2 * var(--row-gap));
  overflow-y: auto;
  padding-right: 5px;

  /* Scrollbar fina */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }
`;

export const DraggableCardContainer = styled.div`
  display: grid;
  transition: all 0.3s ease;

  &[draggable] {
    cursor: move;
  }

  /* Lógica de Alerta Visual (SLA Estourado - Borda e Sombra) */
  ${({ isAlert }) =>
    isAlert &&
    css`
      border: 2px solid ${statusColors.FULLY_OCCUPIED};
      border-radius: 8px;
      /* Usando sintaxe de cor moderna para adicionar transparência ao OKLCH */
      box-shadow: 0 0 8px
        color-mix(in srgb, ${statusColors.FULLY_OCCUPIED}, transparent 60%);
      z-index: 2;
    `}
`;

export const ContentContainer = styled.div`
  grid-column: 2;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  min-width: 0;
`;

export const AgentStatsHeader = styled.div`
  background: ${({ backgroundColor }) => backgroundColor};
  padding-inline: 0.5rem;
  padding-block: 0.5rem;
  border-radius: ${({ borderRadius }) => `calc(${borderRadius}/2)`};
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const AgentCirclesContainer = styled.div`
  display: flex;
  column-gap: 0.5rem;
  align-items: center;

  /* Estilo para círculos clicáveis */
  .circle-wrapper {
    transition: transform 0.2s, opacity 0.2s;

    &.clickable {
      cursor: pointer;
      &:hover {
        transform: scale(1.05);
      }
    }

    &.dimmed {
      opacity: 0.4;
    }
  }
`;

export const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;

export const TooltipText = styled.span`
  font-size: 12px;
`;

export const AgentsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.5rem;
  width: 100%;
`;

export const QueueMetricsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  width: 100%;

  @media (min-width: 1500px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const QueueCard = styled.div`
  display: grid;
  grid-template-rows: min-content auto 1fr;
  border-radius: calc(${({ borderRadius }) => borderRadius} / 2);
  border-color: #ddd;
  row-gap: 0.5rem;
  padding: 0.5rem;
  background-color: ${({ backgroundColor }) => backgroundColor || '#fff'};
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
`;

export const QueueTitle = styled.div`
  padding: 0.5rem;
  border-radius: calc(${({ borderRadius }) => borderRadius} / 2);
  font-weight: bold;
  text-align: center;
  color: ${({ color }) => color};
  background-color: rgba(255, 255, 255, 0.8);
`;

export const QueueStatsGrid = styled.div`
  display: grid;
  column-gap: 0.5rem;
  grid-template-columns: repeat(5, 1fr);
  & * {
    min-width: 0;
  }
`;

export const QueueAgentsContainer = styled.div`
  --grid-max-col-count: 4;
  --grid-min-col-size: 200px;
  --grid-gap: 0.5rem;
  --grid-max-col-size: calc(
    (
      (100% - (var(--grid-max-col-count) - 1) * var(--grid-gap)) /
        var(--grid-max-col-count)
    )
  );
  --grid-col-size-calc: calc(
    (100% - var(--grid-gap) * var(--grid-max-col-count)) /
      var(--grid-max-col-count)
  );
  --grid-col-min-size-calc: min(
    100%,
    max(var(--grid-min-col-size), var(--grid-col-size-calc))
  );

  display: grid;
  gap: var(--grid-gap);
  grid-template-columns: repeat(
    auto-fit,
    minmax(var(--grid-col-min-size-calc), var(--grid-max-col-size))
  );
`;

export const HeaderActions = styled.div`
  display: flex;
  column-gap: 1rem;
  align-items: center;
`;

export const SearchContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const SearchIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: center;
  cursor: pointer;
  margin-right: 0.5rem;
  color: white;
`;

export const SearchInput = styled(Input)`
  height: 38px;
  width: ${({ isActive }) => (isActive ? '200px' : '0px')};
  transition: all ease-in-out 0.3s;
  visibility: ${({ isActive }) => (isActive ? 'visible' : 'hidden')};
  padding: ${({ isActive }) => (isActive ? '0.625rem 0.75rem' : '0')};
  margin-right: 0.5rem;
`;

export const NoResultsMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  font-size: 1.2rem;
  color: #666;
  width: 100%;
  text-align: center;
`;

export const AnalyticsSection = styled.section`
  margin-top: 2rem;
  padding-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  min-width: 0;
`;

export const AnalyticsRow = styled.div`
  display: flex;
  gap: 1.5rem;
  width: 100%;
  flex-wrap: wrap;
  min-width: 0;

  & > * {
    flex: 1;
    min-width: 350px;
  }
`;

export const AnalyticsCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  min-width: 0;

  h4 {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
  }
`;

export const RankingList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px dashed #eee;
    font-size: 0.9rem;

    &:last-child {
      border-bottom: none;
    }

    .label {
      font-weight: 500;
      color: #555;
    }

    .value {
      font-weight: 700;
      color: #333;
      background: #f0f2f5;
      padding: 2px 8px;
      border-radius: 12px;
    }
  }
`;

export const MatrixTableWrapper = styled(AnalyticsCard)`
  overflow: hidden;
  padding: 0;

  h4 {
    margin: 1rem;
  }

  /* Ajustes finos para o container da tabela */
  .flex-table-container {
    padding: 0 1rem 1rem 1rem;
  }
`;

export const VerticalSeparator = styled.div`
  width: 1px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.3);
  margin: 0 0.5rem;
`;

export const TotalLabel = styled.span`
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  margin-left: 0.5rem;
`;
